import { Briefcase, Building2, ChartLine, LayoutGrid, ListTodo, Settings, ShieldCheck } from "lucide-react";
import type { CurrentUser } from "../auth/useCurrentUser";
import { frameworkColor, frameworkLabel } from "./frameworkMeta";
import type { NavContextBlock, NavItem, NavModel, NavSection } from "./types";

// ── Consultant shell navigation ───────────────────────────────────────────
// Builds the sections of docs/NAVIGATION.md section 5, organised by task:
// Today (one ranked list across every client, the home page), Clients,
// Practice and Firm settings - each area a single row with its pages as tabs
// above the page (nav/areas.ts). Firm-level items are gated on the
// consultant's resolved permissions (firm:admin / firm:users), Platform Admin
// on isPlatformOwner (the owner firm *and* an Abacas tenant address, decided
// by the API), the auditor switch on canActAsAuditor. With a client selected
// the header becomes the client context block and a "Client" section
// (What's next, Client portal) leads the list.

export interface RecentClient {
  id: string;
  name: string;
}

/** The light client record the shell keeps for the context block and switcher (GET /consultant/clients). */
export interface ConsultantNavClient {
  id: string;
  name: string;
  /** Service tier name, e.g. "Level 4". */
  tier?: string | null;
  frameworks?: string[];
  latestScore?: number | null;
}

export interface ConsultantNavInput {
  user: CurrentUser;
  /** The consultant's resolved permission set (user.permissions). */
  permissions: string[];
  /** Firm name from branding, falling back to "Abacas". */
  firmName: string;
  firmLogoUrl?: string | null;
  /** The consultancy's own organisation, once known. */
  selfOrg: { id: string; name: string } | null;
  canActAsAuditor: boolean;
  recentClients: RecentClient[];
  /** The client the current route is scoped to (/consultant/clients/:orgId/*). */
  selectedClient: ConsultantNavClient | null;
  /** Own-company portal (POST /auth/select-role client), or the set-up dialog when no self-org exists. */
  onOpenOwnOrg: () => void;
  /** Auditor portal switch (POST /auth/select-role auditor). */
  onOpenAuditorPortal: () => void;
}

/** Home is Today: one ranked list of what needs the consultant across every client. */
export const CONSULTANT_HOME = "/consultant/actions";

export function clientOverviewPath(orgId: string): string {
  return `/consultant/clients/${orgId}`;
}

export function clientPortalPath(orgId: string): string {
  return `/consultant/clients/${orgId}/portal`;
}

export function clientContextBlock(client: ConsultantNavClient): NavContextBlock {
  const frameworks = client.frameworks ?? [];
  return {
    initial: (client.name.trim().charAt(0) || "?").toUpperCase(),
    title: client.name,
    subtitle: client.tier ?? undefined,
    dots: frameworks.map((id) => ({ color: frameworkColor(id), label: frameworkLabel(id) })),
    score: client.latestScore ?? null,
    backTo: "/consultant/clients",
    backLabel: "All clients",
  };
}

export function buildConsultantNav(input: ConsultantNavInput): NavModel {
  const { user, permissions, firmName, firmLogoUrl, selfOrg, canActAsAuditor, recentClients, selectedClient } = input;
  const has = (p: string) => permissions.includes(p);
  const firmAdmin = has("firm:admin");
  const firmUsers = has("firm:users");

  const sections: NavSection[] = [];

  if (selectedClient) {
    sections.push({
      id: "client",
      label: "Client",
      items: [
        // The client management view itself sits under Clients (below); this
        // section keeps only what leaves it for the client's own portal.
        { id: "client-portal", label: "Client portal", to: clientPortalPath(selectedClient.id), icon: LayoutGrid, keywords: [selectedClient.name, "read-through", "view as client"] },
      ],
    });
  }

  sections.push({
    id: "workspace",
    items: [
      {
        id: "today-area", label: "My work", icon: ListTodo, to: CONSULTANT_HOME, badge: "myActions",
        tabs: [
          { id: "actions", label: "Today", to: CONSULTANT_HOME, badge: "myActions", keywords: ["my actions", "tasks", "to do", "attention"] },
          { id: "dashboard", label: "Portfolio", to: "/consultant/dashboard", keywords: ["home", "overview", "portfolio health", "dashboard"] },
          { id: "findings", label: "Findings", to: "/consultant/findings", badge: "openFindings", keywords: ["auditor findings", "open", "acknowledged"] },
          { id: "calendar", label: "Calendar", to: "/consultant/calendar", keywords: ["bookings", "slots", "events", "blocked days"] },
          { id: "activity", label: "Activity", to: "/consultant/activity", keywords: ["audit log", "timeline", "recent"] },
        ],
      },
      // Also active on /consultant/clients/:orgId: managing a client is part of Clients.
      // The read-through portal below it renders its own shell, not this sidebar.
      { id: "clients", label: "Clients", to: "/consultant/clients", icon: Briefcase, badge: "clientsNeedingAction", keywords: ["portfolio", "organisations", "customers"] },
      {
        id: "practice-area", label: "Practice", icon: ChartLine,
        tabs: [
          { id: "reports", label: "Reports", to: "/consultant/reports", keywords: ["compliance status", "certification pipeline", "renewals", "csv"] },
          { id: "utilisation", label: "Utilisation", to: "/consultant/utilisation", keywords: ["capacity", "workload", "billable hours", "booked"] },
          { id: "time", label: "Time", to: "/consultant/time", keywords: ["timesheet", "log time", "hours", "billable"] },
          // Firm money is for firm administrators (the API returns null revenue to everyone else).
          { id: "revenue", label: "Revenue", to: "/consultant/revenue", hidden: !firmAdmin, keywords: ["history", "forecast", "by client", "by consultant"] },
          { id: "billing", label: "Billing", to: "/consultant/billing", hidden: !firmAdmin, keywords: ["revenue", "invoices", "platform fees"] },
          { id: "invitations", label: "Invitations", to: "/consultant/invitations", keywords: ["invites", "pending", "resend"] },
          { id: "groups", label: "Groups", to: "/consultant/groups", keywords: ["group", "subsidiaries", "parent", "holding"] },
          { id: "hardening", label: "CIS hardening", to: "/consultant/hardening", keywords: ["benchmarks", "intune", "baseline"] },
          { id: "rates", label: "Rate card", to: "/consultant/rates", hidden: !firmAdmin, keywords: ["day rates", "grades", "pricing"] },
        ],
      },
      {
        id: "settings-area", label: "Firm settings", icon: Settings,
        tabs: [
          { id: "settings-users", label: "Users", to: "/consultant/settings/users", hidden: !firmUsers, keywords: ["team", "consultants", "staff", "organisations"] },
          { id: "settings-branding", label: "Branding", to: "/consultant/settings/branding", hidden: !firmAdmin, keywords: ["branding & white label", "logo", "colours", "domains", "entra", "white label"] },
          { id: "settings-ai", label: "AI provider", to: "/consultant/settings/ai", hidden: !firmAdmin, keywords: ["anthropic", "byok", "model", "key vault"] },
          { id: "settings-sso", label: "Identity providers", to: "/consultant/settings/sso", hidden: !firmAdmin, keywords: ["sso", "single sign-on", "identity providers", "entra", "google", "saml"] },
          { id: "settings-directory-sync", label: "Directory sync", to: "/consultant/settings/directory-sync", hidden: !firmAdmin, keywords: ["entra groups", "active directory", "roles"] },
          { id: "settings-sign-in", label: "Sign-in policy", to: "/consultant/settings/sign-in", hidden: !firmAdmin, keywords: ["single sign-on", "sso", "microsoft", "google", "password", "tenant"] },
          { id: "settings-accreditation", label: "Auditor accreditation", to: "/consultant/settings/accreditation", hidden: !firmAdmin, keywords: ["bureau", "ukas", "certification body"] },
        ],
      },
    ],
  });

  // The selected client already heads the list as the context block; listing it
  // again under "Recent clients" would highlight two rows for one page.
  const recent = recentClients.filter((c) => c.id !== selectedClient?.id).slice(0, RECENT_CLIENTS_MAX);
  if (recent.length > 0) {
    sections.push({
      id: "recent-clients",
      label: "Recent clients",
      items: recent.map((c) => ({
        id: `recent:${c.id}`,
        label: c.name,
        to: clientOverviewPath(c.id),
        icon: Building2,
        keywords: ["client", "recent"],
      })),
    });
  }

  const switchItems: NavItem[] = [];
  if (selfOrg) {
    switchItems.push({ id: "switch-own-org", label: `${selfOrg.name} portal`, onSelect: input.onOpenOwnOrg, icon: Building2, keywords: ["my company", "own organisation", "client portal"] });
  } else if (firmAdmin) {
    switchItems.push({ id: "switch-setup-own-org", label: "Set up your company's portal", onSelect: input.onOpenOwnOrg, icon: Building2, keywords: ["my company", "own organisation", "self org"] });
  }
  if (canActAsAuditor) {
    switchItems.push({ id: "switch-auditor", label: "Auditor portal", onSelect: input.onOpenAuditorPortal, icon: ShieldCheck, keywords: ["bureau", "audit", "auditor workspace"] });
  }
  sections.push({ id: "switch-workspace", label: "Switch workspace", items: switchItems });

  // Platform Admin is for Abacas staff signed in on an @abacassecurity.onmicrosoft.com account:
  // the API reports isPlatformOwner for exactly them and enforces it (backend lib/platformAdmin.ts).
  sections.push({
    id: "platform",
    label: "Platform",
    items: [
      { id: "platform-admin", label: "Platform Admin", to: "/consultant/platform", icon: ShieldCheck, hidden: !user.isPlatformOwner, keywords: ["consultancies", "bureaux", "revenue", "admin"] },
    ],
  });

  return {
    shellKey: "consultant",
    header: {
      title: firmName,
      subtitle: "Consultant workspace",
      logoUrl: firmLogoUrl ?? null,
      role: { label: "Consultant", tone: "consultant" },
    },
    context: selectedClient ? clientContextBlock(selectedClient) : undefined,
    sections: sections.filter((s) => s.items.some((i) => !i.hidden)),
  };
}

// ── Recent clients (localStorage "nav.recentClients.<userId>") ────────────
// Only organisation ids are persisted, under a key namespaced by the signed-in
// user: names are resolved at render time from the session's own client list,
// so nothing another account could read is ever written, and an id the
// current firm no longer has (or never had) is simply not shown. Sign-out
// (api/client.ts clearLocalSession) removes every key under the prefix.

export const RECENT_CLIENTS_KEY_PREFIX = "nav.recentClients.";
export const RECENT_CLIENTS_MAX = 5;

export function recentClientsKey(userId: string): string {
  return `${RECENT_CLIENTS_KEY_PREFIX}${userId}`;
}

export function readRecentClientIds(userId: string): string[] {
  try {
    const raw = window.localStorage.getItem(recentClientsKey(userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((e): e is string => typeof e === "string").slice(0, RECENT_CLIENTS_MAX);
  } catch {
    return [];
  }
}

function writeRecentClientIds(userId: string, ids: string[]): void {
  try {
    window.localStorage.setItem(recentClientsKey(userId), JSON.stringify(ids));
  } catch {
    /* preference is best effort */
  }
}

/** Moves (or adds) a client to the front of the recent list and persists it. */
export function pushRecentClientId(userId: string, list: string[], id: string): string[] {
  const next = [id, ...list.filter((c) => c !== id)].slice(0, RECENT_CLIENTS_MAX);
  writeRecentClientIds(userId, next);
  return next;
}

/** Drops clients that no longer exist (removed from the portfolio, or never this firm's). */
export function forgetRecentClientIds(userId: string, list: string[], ids: Iterable<string>): string[] {
  const gone = new Set(ids);
  const next = list.filter((c) => !gone.has(c));
  if (next.length !== list.length) writeRecentClientIds(userId, next);
  return next;
}

